/**
 * One marker over time, drawn as plain SVG so the same component serves the
 * patient page, the report preview and the printed PDF. No hooks, no library.
 * Clinic51's bands are the shaded background; each point takes its band's colour.
 */
import type { Band, BandColour, TrendSeries } from '../shared/types.js'

const FILL: Record<BandColour, string> = { teal: '#1f8a8a', green: '#2f8f5b', amber: '#d78a1c', red: '#c0392b', grey: '#9aa09b' }
const TINT: Record<BandColour, string> = { teal: '#dff0f0', green: '#e2f1e8', amber: '#fbeed6', red: '#f6dfdb', grey: '#eceeec' }

export function shortDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' })
}

/** The y range: the values with headroom, widened to show the edges of the bands they sit near. */
function domain(values: number[], bands: Band[]): [number, number] {
  let lo = Math.min(...values)
  let hi = Math.max(...values)
  const span = Math.max(hi - lo, Math.abs(hi) * 0.15, 0.5)
  for (const b of bands) {
    for (const edge of [b.lower, b.upper]) {
      if (edge === null) continue
      if (edge < lo && lo - edge <= span * 1.5) lo = edge
      if (edge > hi && edge - hi <= span * 1.5) hi = edge
    }
  }
  const pad = Math.max((hi - lo) * 0.18, span * 0.12)
  return [lo - pad, hi + pad]
}

export function TrendChart({ series, width = 300, height = 150 }: { series: TrendSeries; width?: number; height?: number }) {
  const pts = series.points
  if (pts.length === 0) return null
  const m = { top: 16, right: 16, bottom: 22, left: 12 }
  const w = width - m.left - m.right
  const h = height - m.top - m.bottom
  const [lo, hi] = domain(pts.map((p) => p.value), series.bands)
  const y = (v: number): number => m.top + h - ((v - lo) / (hi - lo)) * h
  const t = pts.map((p) => new Date(`${p.date}T00:00:00Z`).getTime())
  const t0 = Math.min(...t)
  const t1 = Math.max(...t)
  const x = (i: number): number => (t1 === t0 ? m.left + w / 2 : m.left + (((t[i] ?? t0) - t0) / (t1 - t0)) * w)
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')

  return (
    <svg className="trend" viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img"
      aria-label={`${series.name}: ${pts.map((p) => `${p.valueText} in ${shortDate(p.date)}`).join(', ')}`}>
      {series.bands.map((b) => {
        const top = b.upper === null ? hi : Math.min(b.upper, hi)
        const bottom = b.lower === null ? lo : Math.max(b.lower, lo)
        if (top <= bottom) return null
        return <rect key={b.label} x={m.left} y={y(top)} width={w} height={y(bottom) - y(top)} fill={TINT[b.colour]} />
      })}
      {series.bands.length === 0 && <rect x={m.left} y={m.top} width={w} height={h} fill="#f3f4f1" />}
      {pts.length > 1 && <path d={path} fill="none" stroke="#253617" strokeWidth={1.6} strokeLinejoin="round" />}
      {pts.map((p, i) => (
        <g key={p.assessmentId}>
          <circle cx={x(i)} cy={y(p.value)} r={4} fill={FILL[p.bandColour ?? 'grey']} stroke="#fff" strokeWidth={1.4} />
          <text x={x(i)} y={y(p.value) - 8} textAnchor={i === 0 && pts.length > 1 ? 'start' : i === pts.length - 1 && pts.length > 1 ? 'end' : 'middle'}
            fontSize={10.5} fontWeight={i === pts.length - 1 ? 700 : 400} fill="#1d1f1c" fontFamily="Helvetica, Arial, sans-serif">{p.valueText}</text>
          <text x={x(i)} y={height - 6} textAnchor={i === 0 && pts.length > 1 ? 'start' : i === pts.length - 1 && pts.length > 1 ? 'end' : 'middle'}
            fontSize={9} fill="#6b716c" fontFamily="Helvetica, Arial, sans-serif">{shortDate(p.date)}</text>
        </g>
      ))}
    </svg>
  )
}

/** "down 0.25 since Dec 25", in the marker's own decimals. Null when there is nothing earlier or no change. */
export function changeText(current: number, previous: { date: string; value: number } | null | undefined, decimals: number): string | null {
  if (previous === null || previous === undefined) return null
  const diff = current - previous.value
  const shown = Math.abs(diff).toFixed(decimals)
  if (Number(shown) === 0) return `no change since ${shortDate(previous.date)}`
  return `${diff > 0 ? 'up' : 'down'} ${shown} since ${shortDate(previous.date)}`
}

export function TrendCard({ series }: { series: TrendSeries }) {
  const last = series.points[series.points.length - 1]
  const prev = series.points.length > 1 ? series.points[series.points.length - 2] : null
  if (last === undefined) return null
  const change = changeText(last.value, prev, series.decimals)
  return (
    <div className="trend-card avoid">
      <div className="trend-head">
        <b>{series.name}</b>
        <span className="trend-now">{last.valueText} <small>{series.unit}</small></span>
      </div>
      <div className="trend-sub">
        {last.bandLabel !== null && <span className={`verdict chip ${last.bandColour ?? 'grey'}`}>{last.bandLabel}</span>}
        {change !== null && <span className="muted"> {change}</span>}
      </div>
      <TrendChart series={series} />
    </div>
  )
}
